// Selection is UI state owned here; `changed` also fires when the row key set first resolves or changes,
// so a label bound to the count is right before the first tap. Flutter twin: selectlist.dart.
import { useEffect, useMemo, useRef, useState } from 'react';
import { resetButton, toneColor, toneSoft } from './ui';
import { formatRecent } from '../paths';

const BADGE_TONES = new Set(['accent', 'good', 'warn', 'bad', 'muted', 'neutral']);

function str(v) {
  return v == null ? '' : String(v).trim();
}

function initialOf(title) {
  const ch = [...str(title)].find((c) => /[\p{L}\p{N}]/u.test(c));
  return ch ? ch.toUpperCase() : '';
}

function badgeOf(raw) {
  if (raw == null || raw === '') return null;
  if (typeof raw === 'object') {
    const text = str(raw.text);
    return text ? { text, tone: BADGE_TONES.has(raw.tone) ? raw.tone : 'neutral' } : null;
  }
  const text = str(raw);
  return text ? { text, tone: 'neutral' } : null;
}

function selectListRows(source) {
  if (!Array.isArray(source)) return [];
  const seen = new Set();
  const out = [];
  for (const row of source) {
    if (!row || typeof row !== 'object' || row.key == null) continue;
    const key = String(row.key);
    const title = str(row.title ?? row.label ?? row.name);
    if (!title || seen.has(key)) continue;
    seen.add(key);
    out.push({
      key,
      title,
      subtitle: [str(row.subtitle), row.time ? str(formatRecent(row.time, new Date(), true)) : ''].filter(Boolean).join(' · '),
      badge: badgeOf(row.badge),
      initials: str(row.initials) || initialOf(title),
      media: typeof row.media === 'string' && !row.media.startsWith('media://') ? row.media : '',
      raw: row,
    });
  }
  return out;
}

/** The `changed` payload for a selection over `rows`. */
function selectListSummary(rows, isOn, sumField) {
  const key = [];
  const excluded = [];
  let sum = 0;
  for (const row of rows) {
    if (isOn(row.key)) {
      key.push(row.key);
      if (sumField) sum += Number(row.raw?.[sumField]) || 0;
    } else {
      excluded.push(row.key);
    }
  }
  return { key, excluded, count: key.length, total: rows.length, sum };
}

function pillColors(tone) {
  if (tone === 'neutral' || tone === 'muted') return { background: 'var(--surface-3)', color: 'var(--text-2)' };
  return { background: toneSoft(tone), color: toneColor(tone) };
}

function Check({ on }) {
  return (
    <span aria-hidden style={{
      flex: '0 0 22px', width: 22, height: 22, boxSizing: 'border-box', borderRadius: 6,
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      background: on ? 'var(--text)' : 'var(--surface)',
      border: `1.5px solid ${on ? 'var(--text)' : 'var(--line)'}`,
      color: 'var(--surface)', fontSize: 14, fontWeight: 800, lineHeight: 1,
    }}>{on ? '✓' : ''}</span>
  );
}

export function SelectList({ props = {}, data, emit }) {
  const source = props.items !== undefined ? props.items : data;
  const rows = useMemo(() => selectListRows(source), [source]);
  const initial = props.defaultSelected;
  const startsOn = (key) => (Array.isArray(initial) ? initial.map(String).includes(key) : initial !== 'none');
  const sumField = props.sumField ? String(props.sumField) : '';
  const [flipped, setFlipped] = useState(() => new Set());
  const isOn = (key) => (flipped.has(key) ? !startsOn(key) : startsOn(key));
  const signature = rows.map((r) => r.key).join('\u0001');
  const emitted = useRef(null);

  useEffect(() => {
    if (emitted.current === signature) return;
    emitted.current = signature;
    emit?.('changed', selectListSummary(rows, isOn, sumField));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);

  const toggle = (key) => {
    const next = new Set(flipped);
    if (next.has(key)) next.delete(key); else next.add(key);
    setFlipped(next);
    const on = (k) => (next.has(k) ? !startsOn(k) : startsOn(k));
    emit?.('changed', selectListSummary(rows, on, sumField));
  };

  if (!rows.length) {
    return props.emptyLabel ? (
      <div data-selectlist-empty style={{ padding: 'var(--sp-3)', fontSize: 'var(--t-md)', color: 'var(--text-2)' }}>
        {String(props.emptyLabel)}
      </div>
    ) : null;
  }

  return (
    <div data-selectlist role="group" style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
      {rows.map((row, i) => {
        const on = isOn(row.key);
        const pill = row.badge ? pillColors(row.badge.tone) : null;
        return (
          <button
            key={row.key}
            type="button"
            role="checkbox"
            aria-checked={on ? 'true' : 'false'}
            aria-label={row.title}
            data-row={row.key}
            onClick={() => toggle(row.key)}
            style={{
              ...resetButton,
              display: 'flex', alignItems: 'center', gap: 'var(--sp-3)', width: '100%', minWidth: 0,
              minHeight: 58, padding: 'var(--sp-2) 0', cursor: 'pointer', textAlign: 'start',
              borderTop: i ? '1px solid var(--line-soft)' : 'none',
            }}
          >
            <Check on={on} />
            <span aria-hidden style={{
              flex: '0 0 36px', width: 36, height: 36, borderRadius: '50%', overflow: 'hidden',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              background: 'var(--surface-3)', color: 'var(--text-2)', fontSize: 'var(--t-sm)', fontWeight: 600,
            }}>
              {row.media ? <img src={row.media} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : row.initials}
            </span>
            <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <span style={{
                fontSize: 'var(--t-md)', fontWeight: 600, color: on ? 'var(--text)' : 'var(--text-3)',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>{row.title}</span>
              {row.subtitle ? (
                <span style={{
                  fontSize: 'var(--t-xs)', color: 'var(--text-3)',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>{row.subtitle}</span>
              ) : null}
            </span>
            {row.badge ? (
              <span data-badge style={{
                flex: '0 0 auto', padding: '3px 8px', borderRadius: 'var(--r-pill)',
                fontSize: 'var(--t-xs)', fontWeight: 600, whiteSpace: 'nowrap', ...pill,
              }}>{row.badge.text}</span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
